import React, { useContext, useMemo } from "react";
import axiosInstance from "config/axios";
import { LOGIN_ROUTE } from "config/paths";
import {
    AuthenticationV2Service,
  OrganizationUserService,
  OrganizationService,
  EnrollmentService,
  CertificatesService,
  MenuService,
  ImportService,
  CatalogFilterService,
  CatalogService,
  CourseService,
  SettingService,
} from "services/organizationPortalClient";
import {
  HOME_STUDY_CATALOG_URL,
  LIVE_ONLINE_CATALOG_URL,
  MY_SERIES_URL,
} from "types/myPortalUrls";

type GlobalRouteData = {
  usersUrl: () => string;
  enrollmentsUrl: () => string;
  courseDetailsUrl: (skuId?: string) => string;
  courseDetailsEnrollUsersUrl: (skuId?: string) => string;
  seriesDetailsEnrollUsersUrl: (skuId?: string) => string;
  courseCatalogUrl: () => string;
  liveOnlineCatalogUrl: () => string;
  homeStudyCatalogUrl: () => string;
  seriesUrl: () => string;
  certificatesUrl: () => string;
  orgExamResultUrl: (completionId?: string, userExamId?: string) => string;
  defaultUrl: () => string;
  loginUrl: () => string;
  loginCompleteUrl: () => string;
  unauthorizedUrl: () => string;
  errorUrl: () => string;
  logoutUrl: () => string;
};

type GlobalServices = {
  authenticationService: AuthenticationV2Service;
  organizationUserService: OrganizationUserService;
  organizationService: OrganizationService;
  enrollmentService: EnrollmentService;
  certificatesService: CertificatesService;
  catalogService: CatalogService;
  menuService: MenuService;
  importService: ImportService;
  catalogFilterService: CatalogFilterService;
  courseService: CourseService;
  settingService: SettingService;
};

type GlobalData = GlobalRouteData & GlobalServices;

type GlobalDataProviderProps = {
  children: React.ReactNode;
};

const GlobalDataContext = React.createContext<GlobalData>({} as GlobalData);

export const GlobalDataProvider: React.FC<GlobalDataProviderProps> = ({
  children,
}: GlobalDataProviderProps) => {
  // This allows us to fetch a URL from any component using useGlobalContext()
  // We use functions in a context so that we can easily provide for internationalized URLs (/en/...), should we ever need to, based on the
  // current user's preferences

  const organizationClientServices = useMemo(
    () => ({
      authenticationService: new AuthenticationV2Service("", axiosInstance),
      organizationUserService: new OrganizationUserService("", axiosInstance),
      organizationService: new OrganizationService("", axiosInstance),
      enrollmentService: new EnrollmentService("", axiosInstance),
      certificatesService: new CertificatesService("", axiosInstance),
      catalogService: new CatalogService("", axiosInstance),
      menuService: new MenuService("", axiosInstance),
      importService: new ImportService("", axiosInstance),
      catalogFilterService: new CatalogFilterService("", axiosInstance),
      courseService: new CourseService("", axiosInstance),
      settingService: new SettingService("", axiosInstance),
    }),
    []
  );

  const routeUrls = useMemo(() => {
    return {
      defaultUrl: () => "/",

      /*User*/
      usersUrl: () => "/users",

      /*Enrollments */
      enrollmentsUrl: () => "/enrollments",

      /*Course Catalog */
      courseCatalogUrl: () => "/course-catalog",
      liveOnlineCatalogUrl: () => `/course-catalog${LIVE_ONLINE_CATALOG_URL}`,
      homeStudyCatalogUrl: () => `/course-catalog${HOME_STUDY_CATALOG_URL}`,
      seriesUrl: () => `/course-catalog${MY_SERIES_URL}`,
      courseDetailsUrl: (skuId?: string) =>
        `/course-catalog/course/${skuId ?? ":id"}/details`,
      courseDetailsEnrollUsersUrl: (skuId?: string) =>
        `/course-catalog/course/${skuId ?? ":id"}/details/enroll`,

      seriesDetailsEnrollUsersUrl: (skuId?: string) =>
        `/course-catalog/series/${skuId ?? ":id"}/details/enroll`,

      /*Certificates */
      certificatesUrl: () => "/certificates",
      orgExamResultUrl: (completionId?: string, userExamId?: string) =>
        `/certificates/${
          completionId
            ? encodeURIComponent(completionId)
            : ":completionId"
        }/exam-results/${
          userExamId ? encodeURIComponent(userExamId) : ":userExamId"
        }`,

      /*Authentication */
      loginUrl: () => LOGIN_ROUTE,
      loginCompleteUrl: () => "/logincomplete",

      /*Fallback pages */
      unauthorizedUrl: () => "/unauthorized",
      errorUrl: () => "/error",
      logoutUrl: () => "/account/logout",
    };
  }, []);

  return useMemo(
    () => (
      <GlobalDataContext.Provider
        value={{ ...routeUrls, ...organizationClientServices }}
      >
        {children}
      </GlobalDataContext.Provider>
    ),
    [children, organizationClientServices, routeUrls]
  );
};

const useGlobalContext = () => useContext<GlobalData>(GlobalDataContext);

export default useGlobalContext;
